import { ROUTE_LINE_SHADOW_BLUR, ROUTE_LINE_SHADOW_TRANSLATE } from "./line-paint.ts";
import {
  DEFAULT_QUEST_ROUTE_WIDTH,
  routePaintForMapView,
  type RoutePaintFeature,
} from "./quest-route-paint.ts";
import { routeLineScaleFactor, ROUTE_LINE_WIDTH_FLOOR_PX } from "./route-line-scale.ts";
import type { Insets } from "./rotated-fit.ts";
import {
  QUEST_SPOT_DOM_PHOTO_PX,
  QUEST_SPOT_DOM_TITLE_GAP_PX,
  QUEST_SPOT_DOM_TITLE_MAX_PX,
} from "./quest-spot-map-fit.ts";
import type { LngLat } from "./types.ts";

/** Min px between drawn art (marker, label, stroke) and the map edge or a panel. */
export const HOME_QUEST_FIT_EDGE_PAD = 40;
/** Nudge past MapLibre/cameraForBounds float bleed at the padded edge (px). */
export const HOME_QUEST_FIT_CAMERA_PAD_FUDGE_PX = 3;

/** Bottom reserve for zoom + compass/home stack and attribution (px). */
export const HOME_MAP_CTRL_BOTTOM_RESERVE_PX = 112;
/** Right reserve when controls sit in the clear strip beside the detail panel (px). */
export const HOME_MAP_CTRL_RIGHT_RESERVE_PX = 44;

export type HomeQuestCameraPadding = {
  top: number;
  bottom: number;
  left: number;
  right: number;
};

const measuredSpotTitleWidths = new Map<string, number>();

/**
 * Width of a title measured from the real marker CSS (max-width 160, nowrap, ellipsis).
 * The fit reads this instead of the per-character estimate when the click has measured it.
 */
export function rememberHomeSpotTitleWidth(title: string, widthPx: number): void {
  const text = title.trim();
  if (!text || !Number.isFinite(widthPx) || widthPx <= 0) return;
  measuredSpotTitleWidths.set(text, Math.min(QUEST_SPOT_DOM_TITLE_MAX_PX, widthPx));
}

/** Screen width of one spot title, capped at the marker's CSS max-width. */
export function homeSpotTitleWidthPx(title: string | null | undefined): number {
  const text = (title ?? "").trim();
  if (!text) return 0;
  const measured = measuredSpotTitleWidths.get(text);
  if (measured != null) return measured;
  let px = 0;
  for (const ch of text) px += ch.charCodeAt(0) > 0xff ? 12 : 7;
  return Math.min(QUEST_SPOT_DOM_TITLE_MAX_PX, px);
}

/** Extra px beyond vertex coords for route stroke, shadow, round caps, and fill antialias. */
export function homeRouteFitVisualPadPx(
  paint: readonly RoutePaintFeature[] | null | undefined,
  widthPx: number,
  heightPx: number,
): number {
  const factor = routeLineScaleFactor(widthPx, heightPx);
  let maxW = DEFAULT_QUEST_ROUTE_WIDTH;
  for (const f of routePaintForMapView(paint ?? null) ?? []) {
    if (Number.isFinite(f.properties.width)) maxW = Math.max(maxW, f.properties.width);
  }
  const shadowHalf = (maxW + 3) / 2;
  const half = Math.max(maxW / 2, shadowHalf) + ROUTE_LINE_SHADOW_BLUR + Math.abs(ROUTE_LINE_SHADOW_TRANSLATE[1]) + 2;
  const scaled = factor >= 0.999 ? half : Math.max(ROUTE_LINE_WIDTH_FLOOR_PX, half * factor);
  // A bad width must not swallow the viewport. Real strokes are a few pixels.
  return Math.min(16, scaled);
}

/** Pixels the drawn quest extends past its fit points (stroke, photo, title). */
export function homeQuestFitChrome(
  spotMode: boolean,
  width: number,
  height: number,
  routePaint?: readonly RoutePaintFeature[] | null,
  spotTitles?: readonly (string | null | undefined)[] | null,
): Insets {
  if (!spotMode) {
    const stroke = homeRouteFitVisualPadPx(routePaint ?? null, width, height);
    return { top: stroke, right: stroke, bottom: stroke, left: stroke };
  }
  const photoR = QUEST_SPOT_DOM_PHOTO_PX / 2 + 2;
  let label = 0;
  for (const title of spotTitles ?? []) label = Math.max(label, homeSpotTitleWidthPx(title));
  if (label > 0) label += QUEST_SPOT_DOM_TITLE_GAP_PX;
  return { top: photoR, right: photoR + label, bottom: photoR, left: photoR };
}

/** Padding for fitRotated / cameraForBounds: panels + edge margin + route stroke or spot DOM chrome. */
export function homeQuestCameraFitPadding(
  insets: Insets,
  width: number,
  height: number,
  spotMode: boolean,
  routePaint?: readonly RoutePaintFeature[] | null,
  spotTitles?: readonly (string | null | undefined)[] | null,
): HomeQuestCameraPadding {
  const edge = HOME_QUEST_FIT_EDGE_PAD;
  const fudge = HOME_QUEST_FIT_CAMERA_PAD_FUDGE_PX;
  if (!spotMode) {
    const stroke = homeRouteFitVisualPadPx(routePaint ?? null, width, height);
    const side = (insetSide: number) => Math.max(insetSide, 0) + edge + fudge + stroke;
    const minTop = edge + fudge + stroke;
    return {
      top: Math.max(side(insets.top), minTop),
      bottom: side(insets.bottom),
      left: side(insets.left),
      right: side(insets.right),
    };
  }
  const photoR = QUEST_SPOT_DOM_PHOTO_PX / 2 + 2;
  let label = 0;
  for (const title of spotTitles ?? []) label = Math.max(label, homeSpotTitleWidthPx(title));
  if (label > 0) label += QUEST_SPOT_DOM_TITLE_GAP_PX;
  return {
    top: insets.top + edge + fudge + photoR,
    bottom: insets.bottom + edge + fudge + photoR,
    left: insets.left + edge + fudge + photoR,
    right: insets.right + edge + fudge + photoR + label,
  };
}

/** Axis-aligned bounds of every lng/lat (MapLibre cameraForBounds). */
export function lngLatAxisBounds(points: readonly LngLat[]): [LngLat, LngLat] | null {
  if (!points.length) return null;
  let minLng = Infinity;
  let minLat = Infinity;
  let maxLng = -Infinity;
  let maxLat = -Infinity;
  for (const [lng, lat] of points) {
    if (!Number.isFinite(lng) || !Number.isFinite(lat)) continue;
    minLng = Math.min(minLng, lng);
    maxLng = Math.max(maxLng, lng);
    minLat = Math.min(minLat, lat);
    maxLat = Math.max(maxLat, lat);
  }
  if (!Number.isFinite(minLng)) return null;
  if (minLng === maxLng) {
    const ε = 0.0005;
    minLng -= ε;
    maxLng += ε;
  }
  if (minLat === maxLat) {
    const ε = 0.0005;
    minLat -= ε;
    maxLat += ε;
  }
  return [
    [minLng, minLat],
    [maxLng, maxLat],
  ];
}

/** All checkpoint lng/lat for home spot framing (includes start/finish). */
export function homeSpotQuestFitPoints(pins: readonly { lng: number; lat: number }[]): LngLat[] {
  const out: LngLat[] = [];
  for (const p of pins) {
    if (!Number.isFinite(p.lng) || !Number.isFinite(p.lat)) continue;
    if (p.lng === 0 && p.lat === 0) continue;
    out.push([p.lng, p.lat]);
  }
  return out;
}
